import { useState, createContext, useContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { getMonth, getYear } from '../helpers/dateHelpers';
import { SearchStateInterface } from '../models/SearchInterface';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';

const pairingDefault = {};

const PairingListContext = createContext({
  loading: true,
  pairingListData: pairingDefault as any,
  getPairingListData: (searchState: SearchStateInterface) => {},
  updateCategoryName: (categoryId: string, categoryName: string, sortOrder?: number) => {},
  moveCategory: (categoryList: any[]) => {}
});

export const PairingListProvider = (props) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pairingListData, setPairingListData] = useState(pairingDefault);
  const {setErrorCode} = useApiError();
  
  const getPairingListData = async (search) => {
    if (!user || !search) {
      return;
    }
    setLoading(true);
    const requestObject = {
      body: {
        year: getYear(search.selectedDate),
        month: getMonth(search.selectedDate),
      }
    };
    try {
      const pairingList = await API.post('crewlife', '/pairings/favorites', requestObject);
      if (!pairingList) {
        setLoading(false);
        return;
      }
      const pairingListWithId = pairingList.listobj
      .map(list => ({
        ...list, 
        pairinglist: list.pairinglist.map((p, i) => ({...p, id: i}))
      }));
      setPairingListData({...pairingList, listobj: pairingListWithId});
    } catch (e:any) {
      if( e.response?.status) { setErrorCode(e.response?.status);  }
      else { setErrorCode(500);  }
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryName = async (categoryId, categoryName, sortOrder = 1) => {
    if (!categoryId || !categoryName) {
      return;
    }
    try {
      setLoading(true);
      const requestObject = {
        body: {
          categories: [{
            id: categoryId,
            name: categoryName
          }]
        }
      };
      await API.post('crewlife', '/category/update', requestObject);
      setLoading(false);
    } catch {
      setLoading(false);
    } finally {
      setLoading(false);
    }

  }

  const moveCategory = async (categoryList: any[]) => {
    if (!categoryList) {
      return;
    }
    setLoading(true);
    let requestObject: any = {
      body: {
        categories: [
        ...categoryList
        ]
      }
    };
    try {
      await API.post('crewlife', '/category/move', requestObject);
    } catch (e:any) {
      if( e.response?.status) {  setErrorCode(e.response?.status);  }
      else { setErrorCode(error.DEFAULT); }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PairingListContext.Provider value={{
      loading,
      pairingListData,
      getPairingListData,
      updateCategoryName,
      moveCategory
    }} >
      {props.children}
    </PairingListContext.Provider>
  );
}

export const usePairingList = () => {
  return useContext(PairingListContext);
}
